import { FormLabel, TagSelect, Textinput } from "shared";
import { ComponentTypes, IFieldList } from "entities/template";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { EmployeeWordFields } from "../../../../lib/const/employee_word_fields";
import { YandexWordFields } from "../../../../lib/const/yandex_word_fields";
import {
    isExcludedLegalYandexFields,
    LegalYandexWordFields
} from "../../../../lib/const/legal_yandex_word_fields";
import { fullYandexLegalFields } from "../../../../lib/utils/field_list";
import { VendorFields } from "../../../../lib/const/vendor_fields";
import styles from "./field_list.module.scss";

interface IFieldListProps {
    type: ComponentTypes.hrPerson | ComponentTypes.yandex | ComponentTypes.legalYandex | ComponentTypes.counterparty
    suffix?: string;
    showSubLabel?: boolean;
    required: boolean;
    triggerValidate: number;
    fieldList?: string[];
    onChange: (value: IFieldList) => void

}

export const FieldList: React.FC<IFieldListProps> = React.memo(({
                                                                    type,
                                                                    suffix,
                                                                    fieldList,
                                                                    required,
                                                                    triggerValidate,
                                                                    onChange,
                                                                    showSubLabel = true
                                                                }) => {
    const t = useComponentsTranslations();
    const { i18n } = useTranslation();
    const getTagSelectOptions = () => {
        switch (type) {
            case ComponentTypes.hrPerson:
                return constructHrOptions;
            case ComponentTypes.yandex:
                return constructYandexOptions;
            case ComponentTypes.legalYandex:
                return constructLegalYandexOptions;
            case ComponentTypes.counterparty:
                return constructVendorOptions;
            default:
                return [];
        }
    }

    const constructHrOptions = useMemo(() => {
        return Object.keys(EmployeeWordFields).map((key) => {
            return {
                value: key,
                tagLabel: t(`hrFields.${key as EmployeeWordFields}`),
                label: t(`hrFields.${key as EmployeeWordFields}`) + ` (${key}${suffix ? suffix : ""})`
            }
        })
    }, [suffix, i18n.language])

    const constructYandexOptions = useMemo(() => {
        return Object.keys(YandexWordFields).map((key) => {
            return {
                value: key,
                tagLabel: t(`yandexFields.${key as YandexWordFields}`),
                label: t(`yandexFields.${key as YandexWordFields}`) + ` (${key}${suffix ? suffix : ""})`
            }
        })
    }, [suffix, i18n.language])

    const constructLegalYandexOptions = useMemo(() => {
        return fullYandexLegalFields
            .filter((key) => !isExcludedLegalYandexFields(key))
            .map((key) => {
                return {
                    value: key,
                    tagLabel: t(`legalYandexFields.${key as LegalYandexWordFields}`),
                    label: t(`legalYandexFields.${key as LegalYandexWordFields}`) + ` (${key}${suffix ? suffix : ""})`
                }
            });
    }, [suffix, i18n.language])

    const constructVendorOptions = useMemo(() => {
        return Object.keys(VendorFields).map((key) => {
            return {
                value: key,
                tagLabel: t(`vendorFields.${key as VendorFields}`),
                label: t(`vendorFields.${key as VendorFields}`) + ` (${key}${suffix ? suffix : ""})`
            }
        })
    }, [suffix, i18n.language])


    return <div className={styles.container}>
        <FormLabel title={t("suffix")}>
            <Textinput size="m"
                       value={suffix ? suffix : ""}
                       onChange={(e) => onChange({ suffix: e.target.value })}
                       view="outline"
                       placeholder={`${t("suffixPlaceholder")}${showSubLabel ? " *" : ""}`}/>
            {showSubLabel && <div className={styles.subLabel}>{t("suffixSubLabel")}</div>}
        </FormLabel>
        <TagSelect value={fieldList ? fieldList : []} options={getTagSelectOptions()}
                   validate={required}
                   triggerValidate={triggerValidate}
                   placeholder={t("fieldList")}
                   onChange={(value) => onChange({ fieldsList: value })}/>
    </div>
});
